import { useCallback, useEffect, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { isAfter, isWeekend } from 'date-fns'

import { getTMinus, getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'

import { Page } from '@/components/Page'
import { OPERATIONS } from '@/constants/links'
import { useFunds } from '@/react-query/queries/funds/funds'
import { queryKeys } from '@/react-query/queries/funds/keys'

const styling_rules = {
  light_pos: 'Math.abs(x) <= 1',
  light_mid: 'Math.abs(x) > 1 & Math.abs(x) <= 25',
  light_neg: 'Math.abs(x) > 25',
}

const integerFormatter = numberFormatter({
  delimiter: true,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const positionsColDefs = createColDefs([
  {
    field: 'fund_name',
    headerName: 'Fund',
    filter: false,
    width: 90,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'reconciliation',
    hide: true,
    rowGroup: true,
  },
  {
    field: 'instrument_id',
    hide: true,
  },
  {
    field: 'unique_identifier',
    headerName: 'Unique Identifier',
    width: 200,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'structurename',
    headerName: 'Structurename',
    width: 150,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'instrument_type',
    headerName: 'Instrument Type',
    width: 135,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'position',
    headerName: 'New DB Qty',
    filter: false,
    width: 120,
    cellStyle: { textAlign: 'center' },
    valueFormatter: integerFormatter,
  },
  {
    field: 'notional',
    headerName: 'Old DB Qty',
    filter: false,
    width: 120,
    cellStyle: { textAlign: 'center' },
    valueFormatter: integerFormatter,
  },
  {
    field: 'diff',
    headerName: 'Diff',
    filter: false,
    width: 120,
    cellStyle: { textAlign: 'center' },
    cellClassRules: styling_rules,
    valueFormatter: integerFormatter,
  },
])

const pnlColDefs = createColDefs([
  {
    field: 'fund_name',
    headerName: 'Fund',
    filter: false,
    width: 90,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'reconciliation',
    hide: true,
    rowGroup: true,
  },
  {
    field: 'instrument_id',
    hide: true,
  },
  {
    field: 'unique_identifier',
    headerName: 'Unique Identifier',
    width: 200,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'structurename',
    headerName: 'Structurename',
    width: 150,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'instrument_type',
    headerName: 'Instrument Type',
    width: 135,
    cellStyle: { textAlign: 'center' },
  },

  {
    field: 'pnl',
    headerName: 'Tech P&L',
    filter: false,
    width: 120,
    cellStyle: { textAlign: 'center' },
    valueFormatter: integerFormatter,
  },
  {
    field: 'ops_pnl',
    headerName: 'Ops P&L',
    filter: false,
    width: 120,
    cellStyle: { textAlign: 'center' },
    valueFormatter: integerFormatter,
  },
  {
    field: 'diff',
    headerName: 'Diff',
    filter: false,
    width: 120,
    cellStyle: { textAlign: 'center' },
    cellClassRules: styling_rules,
    valueFormatter: integerFormatter,
  },
])

export interface PositionsPnLPageProps {}

export const PositionsPnLPage: React.FC<PositionsPnLPageProps> = ({}) => {
  const [fund_name, setFundName] = useState<string | undefined>()
  const [value_date, setValueDate] = useState(getTMinus1())
  const [start_date, setStartDate] = useState(getTMinus(2))

  const { data: funds, query: fundsQuery } = useFunds()

  useEffect(() => {
    if (funds.length && !fund_name) {
      setFundName(funds?.[0].fund_name)
    }
  }, [funds])

  const params = {
    value_date: toISO8601(value_date),
    value_date_mo: toISO8601(start_date),
  }

  const paramsWithFundName = {
    ...params,
    fund_name: fund_name!,
  }

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: queryKeys.positions({ value_date: params.value_date, fund_name: fund_name! }),
        queryFn: () => {
          return api.funds.positions.getPositions({
            value_date: params.value_date,
            fund_name: fund_name!,
          })
        },
        enabled: !!fund_name,
      },
      {
        queryKey: queryKeys.positionsPnL(paramsWithFundName),
        queryFn: () => {
          return api.funds.positions.getPnL(paramsWithFundName)
        },
        enabled: !!fund_name,
      },
    ],
  })

  const [positionsQuery, pnlQuery] = queries

  const queriesAreFetching = queries.some((query) => query.isFetching)
  const inputsDisabled = queriesAreFetching

  const isDateDisabled = useCallback((date: Date) => {
    return isAfter(date, new Date()) || isWeekend(date)
  }, [])

  return (
    <Page
      metadata={{ title: 'Positions P&L' }}
      permissions={OPERATIONS.DB_REC.POSITIONS_PNL.permissions}
    >
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <FormItem>
            <Label>Value Date</Label>
            <DatePicker
              size="sm"
              buttonDisabled={inputsDisabled}
              disabled={isDateDisabled}
              selected={value_date}
              onSelect={(date) => date && setValueDate(date)}
            />
          </FormItem>
          <FormItem>
            <Label>Start Date</Label>
            <DatePicker
              size="sm"
              buttonDisabled={inputsDisabled}
              disabled={isDateDisabled}
              selected={start_date}
              onSelect={(date) => date && setStartDate(date)}
            />
          </FormItem>
          <FormItem>
            <Label className={'text-xs'}>Fund</Label>
            <SearchSelect
              size="sm"
              value={fund_name}
              loading={fundsQuery.isFetching}
              placeholder={'Select a fund...'}
              setValue={setFundName}
              disabled={inputsDisabled}
              className={'min-w-auto! w-40!'}
              options={funds.map((fund) => ({ label: fund.fund_name, value: fund.fund_name }))}
            />
          </FormItem>
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'horizontal'} autoSaveId={'conditional'}>
        <ResizablePanel order={1} id="positions-panel" minSize={5}>
          <Widget className={'h-full'} exposed>
            <WidgetHeaderWithQuery
              title={`Reconciliation - Funds Positions`}
              query={positionsQuery}
            />
            <WidgetContent loading={positionsQuery.isLoading || fundsQuery.isLoading}>
              <CustomAgGrid
                domLayout={'normal'}
                columnDefs={positionsColDefs}
                rowData={positionsQuery.data ?? []}
              />
            </WidgetContent>
          </Widget>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel order={2} id="pnl-panel" minSize={5}>
          <Widget className={'h-full'} exposed>
            <WidgetHeaderWithQuery title={`Reconciliation - P&L`} query={pnlQuery} />
            <WidgetContent loading={pnlQuery.isLoading || fundsQuery.isLoading}>
              <CustomAgGrid
                domLayout={'normal'}
                columnDefs={pnlColDefs}
                rowData={pnlQuery.data ?? []}
              />
            </WidgetContent>
          </Widget>
        </ResizablePanel>
      </ResizablePanelGroup>
    </Page>
  )
}
